/**
 * Visitor parking (/visitor-parking) — V0.3 marquee Tier 1.
 *
 * Active visitor sessions ("who's parked where right now") + history.
 *
 * Backend contracts:
 *   GET  /v1/parking/visitor-sessions?active=true|false&cursor&limit
 *   POST /v1/parking/visitor-sessions                              start
 *   POST /v1/parking/visitor-sessions/:id/end                      end
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CarFront,
  Plus,
  StopCircle,
  ClipboardList,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';
import type {
  VisitorParkingSession,
  ParkingSlot,
  UnitLite,
  CursorList
} from './_parking-types';
import { SLOT_TYPE_LABEL } from './_parking-types';

type Tab = 'active' | 'history';

const StartSchema = z.object({
  slot_id: z.string().min(1, 'Slot required'),
  vehicle_plate: z.string().trim().min(3, 'Plate required').max(20),
  visitor_name: z.string().trim().max(120).optional().or(z.literal('')),
  visiting_unit_id: z.string().optional().or(z.literal('')),
  expected_until: z.string().optional().or(z.literal('')),
  notes: z.string().trim().max(500).optional().or(z.literal(''))
});
type StartForm = z.infer<typeof StartSchema>;

export function VisitorParkingPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('active');

  const filtersHash = useMemo(() => JSON.stringify([tab]), [tab]);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQ = useQuery({
    queryKey: ['visitor-parking-sessions', tab, cursor],
    queryFn: () =>
      get<CursorList<VisitorParkingSession>>('/parking/visitor-sessions', {
        params: {
          limit: 50,
          ...(tab === 'active' ? { active: true } : { active: false }),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<VisitorParkingSession>(listQ.data, cursor, filtersHash);
  const sessions = paginator.items;

  // Visitor-eligible slots — slot_type=visitor + status=available.
  const visitorSlotsQ = useQuery({
    queryKey: ['parking-slots', 'visitor-available'],
    queryFn: () =>
      get<CursorList<ParkingSlot>>('/parking/slots', {
        params: { slot_type: 'visitor', status: 'available', limit: 200 }
      }),
    staleTime: 30_000
  });

  const unitsQ = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    staleTime: 5 * 60 * 1000
  });

  // Start session dialog.
  const [startOpen, setStartOpen] = useState(false);
  const startForm = useForm<StartForm>({
    resolver: zodResolver(StartSchema),
    mode: 'onChange',
    defaultValues: {
      slot_id: '',
      vehicle_plate: '',
      visitor_name: '',
      visiting_unit_id: '',
      expected_until: '',
      notes: ''
    }
  });
  const start = useMutation({
    mutationFn: (v: StartForm) =>
      post<VisitorParkingSession>('/parking/visitor-sessions', {
        slot_id: v.slot_id,
        vehicle_plate: v.vehicle_plate,
        visitor_name: v.visitor_name?.trim() || null,
        visiting_unit_id: v.visiting_unit_id || null,
        expected_until: v.expected_until || null,
        notes: v.notes?.trim() || null
      }),
    onSuccess: () => {
      toast.success('Visitor parked');
      setStartOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['visitor-parking-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [endTarget, setEndTarget] = useState<VisitorParkingSession | null>(null);
  const end = useMutation({
    mutationFn: (id: string) => post<VisitorParkingSession>(`/parking/visitor-sessions/${id}/end`, {}),
    onSuccess: () => {
      toast.success('Visitor session ended');
      setEndTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['visitor-parking-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="Visitor parking"
        description="Active visitor parking sessions and history."
        actions={
          <Button size="sm" onClick={() => setStartOpen(true)}>
            <Plus className="h-4 w-4" /> Start session
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'history', label: 'History' }
              ]}
              aria-label="Visitor parking sections"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-vt-4">
            {listQ.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : listQ.isError ? (
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Could not load sessions"
                description={getErrorMessage(listQ.error)}
              />
            ) : sessions.length === 0 ? (
              <EmptyState
                icon={tab === 'active' ? <CarFront className="h-6 w-6" /> : <ClipboardList className="h-6 w-6" />}
                title={tab === 'active' ? 'No active visitor parking' : 'No history yet'}
                description={
                  tab === 'active'
                    ? 'Start a session when a visitor parks.'
                    : 'Ended sessions will appear here.'
                }
                action={
                  tab === 'active' ? (
                    <Button onClick={() => setStartOpen(true)}>
                      <Plus className="h-4 w-4" /> Start session
                    </Button>
                  ) : null
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plate</TableHead>
                    <TableHead>Visitor</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Visiting unit</TableHead>
                    <TableHead>Parked since</TableHead>
                    <TableHead>Expected until</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-vt-13 font-medium text-vt-text">
                        {s.vehicle_plate}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {s.visitor_name ?? '—'}
                      </TableCell>
                      <TableCell className="font-mono text-vt-13 text-vt-text-muted">
                        {s.slot?.slot_number ?? '—'}
                        {s.slot?.zone?.name ? ` · ${s.slot.zone.name}` : ''}
                      </TableCell>
                      <TableCell className="text-vt-13 text-vt-text-muted">
                        {s.visiting_unit?.number ?? '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                        {formatDateTime(s.parked_at)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                        {s.expected_until ? formatDateTime(s.expected_until) : '—'}
                      </TableCell>
                      <TableCell>
                        {s.status === 'active' ? (
                          <Badge variant="ok">Active</Badge>
                        ) : (
                          <Badge variant="muted">Ended</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="End session"
                            onClick={() => setEndTarget(s)}
                          >
                            <StopCircle className="h-4 w-4" /> End
                          </Button>
                        ) : (
                          <span className="text-vt-12 text-vt-text-muted">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQ.isFetching}
              itemsShown={sessions.length}
              pageSize={50}
              onLoadMore={() => {
                if (paginator.nextCursor) setCursor(paginator.nextCursor);
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Start session dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start visitor parking session</DialogTitle>
            <DialogDescription>
              Pick a visitor slot and enter the vehicle plate. Linking to a visiting unit makes
              this session show up on the unit's activity timeline.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={startForm.handleSubmit((v) => start.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="vp-slot">Slot</Label>
                <Select id="vp-slot" {...startForm.register('slot_id')}>
                  <option value="">— Pick visitor slot —</option>
                  {(visitorSlotsQ.data?.items ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.zone?.code ? `${s.zone.code} · ` : ''}
                      {s.slot_number} ({SLOT_TYPE_LABEL[s.slot_type]})
                    </option>
                  ))}
                </Select>
                {startForm.formState.errors.slot_id ? (
                  <p className="text-vt-12 text-vt-alert">{startForm.formState.errors.slot_id.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="vp-plate">Vehicle plate</Label>
                <Input id="vp-plate" placeholder="DL 7C AB 1234" {...startForm.register('vehicle_plate')} />
                {startForm.formState.errors.vehicle_plate ? (
                  <p className="text-vt-12 text-vt-alert">{startForm.formState.errors.vehicle_plate.message}</p>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="vp-name">Visitor name</Label>
                <Input id="vp-name" {...startForm.register('visitor_name')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="vp-unit">Visiting unit</Label>
                <Select id="vp-unit" {...startForm.register('visiting_unit_id')}>
                  <option value="">— None —</option>
                  {(unitsQ.data?.items ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.block ? `${u.block.name} · ` : ''}
                      {u.number}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="vp-until">Expected until</Label>
              <Input id="vp-until" type="datetime-local" {...startForm.register('expected_until')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="vp-notes">Notes</Label>
              <Textarea id="vp-notes" rows={2} {...startForm.register('notes')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStartOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={start.isPending}
                loadingText="Starting…"
                disabled={start.isPending || !startForm.formState.isValid}
              >
                Start session
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(endTarget)}
        onOpenChange={(v) => (v ? null : setEndTarget(null))}
        title="End visitor session"
        description={
          endTarget
            ? `End the session for ${endTarget.vehicle_plate}? The slot becomes available again.`
            : null
        }
        confirmLabel="End session"
        variant="warn"
        loading={end.isPending}
        onConfirm={() => {
          if (endTarget) end.mutate(endTarget.id);
        }}
      />
    </>
  );
}
